import { useEffect, useRef } from "react";
import { apiErrorMessage } from "@/lib/api-error";
import { toast } from "@/lib/toast";

/**
 * Raises an error toast when a query starts failing.
 *
 * For the queries that feed something secondary — a filter dropdown, a role
 * checklist, the inbox badge — where the screen has no natural place for an
 * error state and would otherwise just come up empty, which reads as "there is
 * nothing" rather than "this did not load".
 *
 * Once per failure streak, not once per attempt: a polled query that keeps
 * failing (the unread count, every 20 seconds) must not raise a toast on
 * every poll. It fires again only after the query has succeeded in between.
 */
export function useQueryErrorToast(
  result: { isError: boolean; error?: unknown },
  title: string,
): void {
  const { isError, error } = result;
  const raised = useRef(false);

  useEffect(() => {
    if (!isError) {
      raised.current = false;
      return;
    }
    if (raised.current) return;
    raised.current = true;
    toast.error(title, { description: apiErrorMessage(error) });
  }, [isError, error, title]);
}
